import { useEffect, useState, useCallback, useRef } from "react";

import QueryString from "query-string";

import { SearchService } from "services";

import SearchPager from "./SearchPager";
import SearchField from "./SearchField";
import SearchResults from "./SearchResults";

let searchTimeout = null;

const Search = () => {
  const isFirstRun = useRef(true);

  const [searched, setSearched] = useState(false);

  const [search, setSearch] = useState({
    query: "",
    offset: 0,
    amount: 0,
    total: 0,
    items: [],
  });

  const fetchStateFromUrlAndSearch = async () => {
    const { query, offset, amount } = QueryString.parse(window.location.search);

    const response = await SearchService.search(query, offset, amount);

    setSearched(!!query);
    setSearch({
      query: query || "",
      offset: offset || 0,
      amount: amount || 10,
      total: response.total || 0,
      items: response.results || [],
    });
  };

  const handleUpdateQuery = (query) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      query,
      offset: 0,
    }));
  };

  const handleSearch = useCallback(
    async (nextQuery) => {
      const query = nextQuery !== undefined ? nextQuery : search.query;
      const base = window.location.toString().split("?")[0];
      const searchUrl = QueryString.stringify({
        query,
        offset: search.offset,
        amount: search.amount,
      });

      history.pushState({}, "", `${base}?${searchUrl}`); // eslint-disable-line no-restricted-globals

      const response = await SearchService.search(
        query,
        search.offset,
        search.amount
      );

      setSearched(!!query);
      setSearch((prevSearch) => ({
        ...prevSearch,
        offset: parseInt(search.offset, 10) || 0,
        amount: parseInt(search.amount, 10) || 10,
        items: response.results || [],
        total: parseInt(response.total, 10) || 0,
      }));
    },
    [search.amount, search.offset, search.query]
  );

  const debounceHandleSearch = (query) => {
    clearTimeout(searchTimeout);
    searchTimeout = window.setTimeout(() => handleSearch(query), 250);
  };

  const handleUpdateQueryAndSearch = (query) => {
    handleUpdateQuery(query);

    if (query.length > 2) {
      debounceHandleSearch(query);
    } else if (query.length === 0) {
      handleSearch(query);
    }
  };

  const handleUpdateOffset = (offset) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      offset,
    }));
  };

  useEffect(() => {
    window.addEventListener("popstate", fetchStateFromUrlAndSearch);
    fetchStateFromUrlAndSearch();

    return () => {
      window.removeEventListener("popstate", fetchStateFromUrlAndSearch);
    };
  }, []);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;

      return;
    }

    handleSearch();
  }, [search.offset, handleSearch]);

  return (
    <div className="search">
      <div className="block">
        <div className="search-field">
          <SearchField
            query={search.query}
            handleUpdateQueryAndSearch={handleUpdateQueryAndSearch}
            handleSearch={handleSearch}
            searched={searched}
            total={search.total}
          />
        </div>
      </div>
      {searched && (
        <>
          <div className="block">
            {search.items && (
              <SearchResults
                searched={searched}
                query={search.query}
                items={search.items}
              />
            )}
          </div>
          <div className="block">
            <SearchPager
              total={parseInt(search.total, 10)}
              offset={parseInt(search.offset, 10)}
              amount={parseInt(search.amount, 10)}
              handleUpdateOffset={handleUpdateOffset}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Search;
